import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
// import MessageModal from "../../components/MessageModal"
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import ForbiddenPage from "../../components/ForbiddenPage";
import { canUserAccessSchedules } from "../../utils/userPermissions";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "moment/locale/pt-br";
import "moment/locale/es";
import "moment/locale/en-gb";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchIcon from "@material-ui/icons/Search";
import "./Schedules.css";

// Defina a função getUrlParam antes de usá-la
function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const localizer = momentLocalizer(moment);

const getScheduleTitle = (schedule) => {
  const contact = schedule?.contact;
  return (
    contact?.name ||
    contact?.number ||
    `Lead #${schedule?.id || ""}`
  );
};

const STATUS_COLORS = {
  agendado: { bg: "#2563eb", border: "#1d4ed8" },
  enviado: { bg: "#16a34a", border: "#15803d" },
  erro: { bg: "#dc2626", border: "#b91c1c" },
  vencido: { bg: "#ea580c", border: "#c2410c" },
};

const getScheduleVisualStatus = (schedule) => {
  const status = String(schedule?.status || "").toLowerCase();
  const sendAt = schedule?.sendAt ? moment(schedule.sendAt) : null;
  const isPast = sendAt && sendAt.isBefore(moment());
  const isSent =
    status === "enviada" ||
    status === "enviado" ||
    Boolean(schedule?.sentAt);

  if (status === "erro") return "erro";
  if (isSent) return "enviado";
  if (isPast) return "vencido";
  return "agendado";
};

const getScheduleStatusLabel = (visualStatus) =>
  i18n.t(`schedules.calendarStatus.${visualStatus}`, {
    defaultValue:
      {
        agendado: "Programado",
        vencido: "Vencido",
        enviado: "Enviado",
        erro: "Error",
      }[visualStatus] || visualStatus,
  });

const ScheduleEvent = ({ event, view }) => {
  const schedule = event.resource;
  const visualStatus = getScheduleVisualStatus(schedule);
  const timeLabel = moment(event.start).format("HH:mm");
  const isMonthView = view === "month";
  const isAgendaView = view === "agenda";
  const messagePreview = schedule?.body
    ? String(schedule.body).replace(/\s+/g, " ").trim()
    : "";

  if (isAgendaView) {
    return (
      <div
        className={`schedule-agenda-card schedule-agenda-card--${visualStatus}`}
        title={[timeLabel, getScheduleTitle(schedule), messagePreview].filter(Boolean).join(" · ")}
      >
        <div className="schedule-agenda-card__header">
          <span className="schedule-agenda-card__name">{event.title}</span>
          <span className={`schedule-agenda-card__badge schedule-agenda-card__badge--${visualStatus}`}>
            {getScheduleStatusLabel(visualStatus)}
          </span>
        </div>
        {messagePreview && (
          <span className="schedule-agenda-card__preview">{messagePreview}</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`schedule-event ${isMonthView ? "schedule-event--month" : ""}`}
      title={[timeLabel, getScheduleTitle(schedule), messagePreview].filter(Boolean).join(" · ")}
    >
      <span className="schedule-event__time">{timeLabel}</span>
      <span className="schedule-event__name">{event.title}</span>
      {!isMonthView && messagePreview && (
        <span className="schedule-event__preview">{messagePreview}</span>
      )}
    </div>
  );
};

const buildCalendarComponents = (view) => ({
  event: (props) => <ScheduleEvent {...props} view={view} />,
});

// Función para obtener los mensajes del calendario según el idioma
const getCalendarMessages = () => {
  const currentLanguage = i18n.language;
  
  if (currentLanguage === 'es') {
    return {
      date: i18n.t("calendar.date"),
      time: i18n.t("calendar.time"),
      event: i18n.t("calendar.event"),
      allDay: i18n.t("calendar.allDay"),
      week: i18n.t("calendar.week"),
      work_week: i18n.t("calendar.work_week"),
      day: i18n.t("calendar.day"),
      month: i18n.t("calendar.month"),
      previous: i18n.t("calendar.previous"),
      next: i18n.t("calendar.next"),
      yesterday: i18n.t("calendar.yesterday"),
      tomorrow: i18n.t("calendar.tomorrow"),
      today: i18n.t("calendar.today"),
      agenda: i18n.t("calendar.agenda"),
      noEventsInRange: i18n.t("calendar.noEventsInRange"),
      showMore: function showMore(total) {
        return "+" + total + " " + i18n.t("calendar.showMore");
      }
    };
  } else if (currentLanguage === 'en') {
    return {
      date: i18n.t("calendar.date"),
      time: i18n.t("calendar.time"),
      event: i18n.t("calendar.event"),
      allDay: i18n.t("calendar.allDay"),
      week: i18n.t("calendar.week"),
      work_week: i18n.t("calendar.work_week"),
      day: i18n.t("calendar.day"),
      month: i18n.t("calendar.month"),
      previous: i18n.t("calendar.previous"),
      next: i18n.t("calendar.next"),
      yesterday: i18n.t("calendar.yesterday"),
      tomorrow: i18n.t("calendar.tomorrow"),
      today: i18n.t("calendar.today"),
      agenda: i18n.t("calendar.agenda"),
      noEventsInRange: i18n.t("calendar.noEventsInRange"),
      showMore: function showMore(total) {
        return "+" + total + " " + i18n.t("calendar.showMore");
      }
    };
  } else {
    // Portugués (idioma por defecto)
    return {
      date: "Data",
      time: "Hora",
      event: "Evento",
      allDay: "Dia Todo",
      week: "Semana",
      work_week: "Agendamentos",
      day: "Dia",
      month: "Mês",
      previous: "Anterior",
      next: "Próximo",
      yesterday: "Ontem",
      tomorrow: "Amanhã",
      today: "Hoje",
      agenda: "Agenda",
      noEventsInRange: "Não há agendamentos no período.",
      showMore: function showMore(total) {
        return "+" + total + " mais";
      }
    };
  }
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.fancyBackground,
    borderRadius: 12,
    overflow: "hidden",
    minHeight: "calc(100vh - 150px)",
    ...theme.scrollbarStyles,
  },
  calendarWrapper: {
    flex: 1,
    minHeight: 560,
  },
}));

const Schedules = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [calendarView, setCalendarView] = useState("month");

  // Configurar el locale de moment según el idioma actual
  useEffect(() => {
    const currentLanguage = i18n.language;
    if (currentLanguage === 'es') {
      moment.locale('es');
    } else if (currentLanguage === 'en') {
      moment.locale('en-gb');
    } else {
      moment.locale('pt-br');
    }
  }, [i18n.language]);

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules || !canUserAccessSchedules(user)) {
        toast.error(i18n.t("schedules.noPermission"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    // handleOpenScheduleModalFromContactId();
    // const socket = socketManager.GetSocket(user.companyId, user.id);


    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    }

    socket.on(`company${user.companyId}-schedule`, onCompanySchedule)

    return () => {
      socket.off(`company${user.companyId}-schedule`, onCompanySchedule)
    };
  }, [socket]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const calendarEvents = schedules.map((schedule) => {
    const start = moment(schedule.sendAt);
    return {
      id: schedule.id,
      title: getScheduleTitle(schedule),
      start: start.toDate(),
      end: start.clone().add(20, "minutes").toDate(),
      resource: schedule,
    };
  });

  const handleSelectEvent = (event) => {
    if (event?.resource) {
      handleEditSchedule(event.resource);
    }
  };

  const getEventPropGetter = (event) => {
    const visualStatus = getScheduleVisualStatus(event?.resource);
    const colors = STATUS_COLORS[visualStatus] || STATUS_COLORS.agendado;

    if (calendarView === "agenda") {
      return {
        className: `schedule-agenda-row schedule-agenda-row--${visualStatus}`,
        style: {
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: "inherit",
        },
      };
    }

    return {
      className: `schedule-event-row schedule-event-row--${visualStatus}`,
      style: {
        backgroundColor: colors.bg,
        borderColor: colors.border,
        color: "#ffffff",
      },
    };
  };

  if (!canUserAccessSchedules(user)) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          // aria-labelledby="form-dialog-title"
          scheduleId={
            selectedSchedule ? selectedSchedule.id : null
          }
          contactId={contactId}
          cleanContact={cleanContact}
        />
      )}
      <MainHeader>
        <Title>{i18n.t("schedules.title")} ({schedules.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
          >
            {i18n.t("schedules.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <div
          className={`schedules-calendar schedules-calendar--${theme.mode === "light" ? "light" : "dark"} ${classes.calendarWrapper}`}
        >
          <Calendar
            messages={getCalendarMessages()}
            formats={{
              agendaDateFormat: "DD/MM ddd",
              agendaTimeFormat: "HH:mm",
              agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
                `${localizer.format(start, "HH:mm", culture)} – ${localizer.format(end, "HH:mm", culture)}`,
              dayFormat: "DD ddd",
              weekdayFormat: "dddd",
              monthHeaderFormat: "MMMM YYYY",
              dayHeaderFormat: "dddd DD/MM",
              dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                `${localizer.format(start, "DD MMM", culture)} – ${localizer.format(end, "DD MMM", culture)}`,
              timeGutterFormat: "HH:mm",
              eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                `${localizer.format(start, "HH:mm", culture)} – ${localizer.format(end, "HH:mm", culture)}`,
            }}
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            views={["month", "week", "day", "agenda"]}
            view={calendarView}
            onView={setCalendarView}
            popup
            selectable
            dayLayoutAlgorithm="no-overlap"
            scrollToTime={new Date(1970, 0, 1, 8, 0, 0)}
            components={buildCalendarComponents(calendarView)}
            eventPropGetter={getEventPropGetter}
            onSelectEvent={handleSelectEvent}
            style={{ height: "100%", minHeight: 560 }}
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Schedules;